import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Button,
    Box,
    Avatar,
    Divider,
    Link,
    CircularProgress
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Home as HomeIcon,
    AccountCircle as AccountCircleIcon,
    Store as StoreIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import { OAuthCallback } from './logic/stripe-api';

const StripeConnectSuccess = () => {
    const navigate = useNavigate();
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const responseError = urlParams.get('error');
    const error_description = urlParams.get('error_description');
    const success = urlParams.get('success');
    const [isProcessing, setIsProcessing] = useState(true);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
   
    const handleOAuthCallback = async () => {
        try {        
            if(code && state){
                const response = await OAuthCallback( code, state, responseError, error_description,navigate);                           
                if(response.isError){
                    setError(true);
                    setErrorMessage(response.message || 'An error occurred during connection');
                    setIsProcessing(false);
                    return;
                }
               setIsProcessing(false);               
            }             
        } catch (err) {
            setErrorMessage(err.message || 'An error occurred during connection');
            setIsProcessing(false);
        }
    };

    const hasExecuted = useRef(false);

    useEffect(() => {
        if (!code || !state) {
            console.warn("Missing OAuth parameters. Redirecting to home page.");
            setIsProcessing(false);
        } else if (!hasExecuted.current) {
            hasExecuted.current = true;
            handleOAuthCallback();
        }
    }, [code, state]);

    const SuccessContent = () => (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Avatar sx={{ bgcolor: 'success.light', width: 80, height: 80 }}>
                        <CheckCircleIcon sx={{ fontSize: 48 }} />
                    </Avatar>
                </Box>

                <Typography variant="h4" gutterBottom>
                    Stripe Account Connected!
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                    Your Stripe account has been successfully connected to our platform.
                    You can now start accepting payments and managing your transactions.
                </Typography>

                <Box sx={{ 
                    backgroundColor: 'success.light', 
                    color: 'success.contrastText',
                    p: 2,
                    borderRadius: 1,
                    my: 3,
                    textAlign: 'left'
                }}>
                    <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <StoreIcon sx={{ mr: 1 }} /> Next Steps:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 24 }}>
                        <li>Set up your payment preferences</li>
                        <li>Configure payout schedules</li>
                        <li>Test your payment flow</li>
                    </ul>
                </Box>

                <Box sx={{ mt: 4, '& > *': { mb: 2 } }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() => navigate('/settings#4')}
                        startIcon={<AccountCircleIcon />}
                        sx={{ mb: 2 }}
                    >
                        Go to Stripe Settings
                    </Button>

                    <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        size="large"
                        onClick={() => navigate('/')}
                        startIcon={<HomeIcon />}
                    >
                        Return to Dashboard
                    </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="body2" color="text.secondary">
                    Need help with your Stripe account? <Link href="https://support.stripe.com/" target="_blank" rel="noopener">Visit Stripe Support</Link>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Or <Link href="mailto:support@yourplatform.com">contact our team</Link> for platform-specific questions.
                </Typography>
            </Paper>
        </Container>
    );

    const ErrorContent = () => (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Avatar sx={{ bgcolor: 'error.light', width: 80, height: 80 }}>
                        <EmailIcon sx={{ fontSize: 48 }} />
                    </Avatar>
                </Box>

                <Typography variant="h4" gutterBottom>
                    Connection Issue
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                    {errorMessage || 'We encountered an issue connecting your Stripe account.'}
                </Typography>

                <Box sx={{ mt: 4, '& > *': { mb: 2 } }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() => window.location.reload()}
                        sx={{ mb: 2 }}
                    >
                        Try Again
                    </Button>

                    <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        size="large"
                        onClick={() => navigate('/')}
                        startIcon={<HomeIcon />}
                    >
                        Return to Dashboard
                    </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="body2" color="text.secondary">
                    Please <Link href="mailto:support@yourplatform.com">contact support</Link> if the issue persists.
                </Typography>
            </Paper>
        </Container>
    );

    const LoadingContent = () => (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <CircularProgress size={80} color="primary" />
                </Box>
                <Typography variant="h5" gutterBottom>
                    Connecting Your Stripe Account
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Please wait while we securely connect your Stripe account...
                </Typography>
            </Paper>
        </Container>
    );

    if (isProcessing) return <LoadingContent />;
    if (responseError || error) return <ErrorContent />;
    return <SuccessContent />;
};

export default StripeConnectSuccess;